import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { courseService } from '../../api/courseService';
import LessonForm from './LessonForm';
import Modal from '../../components/UI/Modal';
import type { Lesson } from '../../api/courseService';

const LessonManagementContainer = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 0.25rem;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
  &.delete {
    border-color: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.error};
    &:hover {
      background-color: ${({ theme }) => theme.colors.error};
      color: white;
    }
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.errorLight};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SortableList = styled.div`
  margin-top: 2rem;
`;

const LessonManagement: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const queryClient = useQueryClient();

  // Fetch course details
  const { data: courseResponse, isLoading: courseLoading, error: courseError } = useQuery({
    queryKey: ['adminCourse', courseId],
    queryFn: () => courseService.getCourseById(courseId || ''),
    enabled: !!courseId,
  });

  // Fetch lessons
  const { data: lessonsResponse, isLoading: lessonsLoading, error: lessonsError } = useQuery({
    queryKey: ['adminLessons', courseId],
    queryFn: () => courseService.getLessons(courseId || ''),
    enabled: !!courseId,
  });

  // Create lesson mutation
  const createMutation = useMutation({
    mutationFn: (lessonData: { title: string; content: string; order: number; courseId: string }) => 
      courseService.createLesson({
        ...lessonData,
        courseId: courseId || '',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLessons', courseId] });
      queryClient.invalidateQueries({ queryKey: ['adminCourse', courseId] });
      setIsCreating(false);
    },
  });

  // Update lesson mutation
  const updateMutation = useMutation({
    mutationFn: ({ lessonId, data }: { lessonId: string; data: Partial<{ title: string; content: string; order: number }> }) => 
      courseService.updateLesson(courseId || '', lessonId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLessons', courseId] });
      setIsEditing(false);
      setSelectedLesson(null);
    },
  });

  // Delete lesson mutation
  const deleteMutation = useMutation({
    mutationFn: (lessonId: string) => courseService.deleteLesson(courseId || '', lessonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminLessons', courseId] });
      queryClient.invalidateQueries({ queryKey: ['adminCourse', courseId] });
    },
  });

  const handleCreateLesson = (data: { title: string; content: string; order: number; video_url?: string; thumbnail_url?: string }) => {
    createMutation.mutate({
      ...data,
      courseId: courseId || '',
    });
  };

  const handleUpdateLesson = (data: { title: string; content: string; order: number; video_url?: string; thumbnail_url?: string }) => {
    if (selectedLesson) {
      updateMutation.mutate({
        lessonId: selectedLesson.id,
        data,
      });
    }
  };

  const handleDeleteLesson = (lessonId: string) => {
    if (window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      deleteMutation.mutate(lessonId);
    }
  };

  const isLoading = courseLoading || lessonsLoading;
  const error = courseError || lessonsError;
  const course = courseResponse?.data?.data;
  const lessons = lessonsResponse?.data?.data || [];

  // Sort lessons by order
  const sortedLessons = [...lessons].sort((a, b) => {
    const orderA = a.order_index ?? a.order ?? 0;
    const orderB = b.order_index ?? b.order ?? 0;
    return orderA - orderB;
  });

  if (isLoading) {
    return <LoadingMessage>Loading lessons...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error loading lessons: {(error as Error).message}</ErrorMessage>;
  }

  if (!course) {
    return <ErrorMessage>Course not found</ErrorMessage>;
  }

  // Calculate the next order value for a new lesson
  const nextOrder = sortedLessons.length > 0
    ? Math.max(...sortedLessons.map(lesson => lesson.order_index ?? lesson.order ?? 0)) + 1
    : 1;

  return (
    <LessonManagementContainer>
      <BackLink to="/admin/courses">
        &larr; Back to Courses
      </BackLink>
      
      <Header>
        <div>
          <h2>Lessons for: {course.title}</h2>
          <p>{course.description}</p>
        </div>
        <Button onClick={() => setIsCreating(true)}>Add New Lesson</Button>
      </Header>

      <Modal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        title="Create New Lesson"
      >
        <LessonForm
          onSubmit={handleCreateLesson}
          onCancel={() => setIsCreating(false)}
          isSubmitting={createMutation.isPending}
          initialOrder={nextOrder}
        />
      </Modal>

      <Modal
        isOpen={isEditing}
        onClose={() => {
          setIsEditing(false);
          setSelectedLesson(null);
        }}
        title="Edit Lesson"
      >
        {selectedLesson && (
          <LessonForm
            lesson={selectedLesson}
            onSubmit={handleUpdateLesson}
            onCancel={() => {
              setIsEditing(false);
              setSelectedLesson(null);
            }}
            isSubmitting={updateMutation.isPending}
          />
        )}
      </Modal>

      {sortedLessons.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Order</Th>
              <Th>Title</Th>
              <Th>Content</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedLessons.map((lesson) => (
              <tr key={lesson.id}>
                <Td>{lesson.order_index ?? lesson.order}</Td>
                <Td>{lesson.title}</Td>
                <Td>{lesson.content.length > 50 ? `${lesson.content.substring(0, 50)}...` : lesson.content}</Td>
                <Td>
                  <ActionButton
                    onClick={() => {
                      setSelectedLesson(lesson);
                      setIsEditing(true);
                    }}
                  >
                    Edit
                  </ActionButton>
                  <ActionButton
                    className="delete"
                    onClick={() => handleDeleteLesson(lesson.id)}
                  >
                    Delete
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No lessons found for this course. Add your first lesson using the button above.</p>
      )}
    </LessonManagementContainer>
  );
};

export default LessonManagement; 